import { APP_INITIALIZER, importProvidersFrom, inject, Injector } from '@angular/core';
import { InMemoryScrollingOptions, provideRouter, withInMemoryScrolling, withPreloading } from '@angular/router';

import { routes } from './app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
    HTTP_INTERCEPTORS,
    HttpClient,
    HttpHandlerFn,
    HttpRequest,
    provideHttpClient,
    withInterceptors,
} from '@angular/common/http';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { environment } from '@environments/environment';
import { LOCATION_INITIALIZED } from '@angular/common';
import { GeneralHelper } from '@shared/helpers';
import { authenticationInterceptor } from '@core/interceptors/authentication.interceptor';
import { MessageService } from 'primeng/api';
import { FlagBasedPreloadingStrategy } from '@core/helpers/preload-routes.helper';

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http);
}

export const provideTranslation = () => ({
    defaultLanguage: 'en',
    loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
    },
});

let previousRoute = '';
const scrollConfig: InMemoryScrollingOptions = {
    get scrollPositionRestoration() {
        if (!previousRoute || previousRoute === window.location.pathname) {
            previousRoute = window.location.pathname;
            return 'disabled' as const;
        }
        previousRoute = window.location.pathname;
        return 'top' as const;
    },
    anchorScrolling: 'enabled',
};

function loadWithRetry(loadFn: () => Promise<any>, maxRetries = 3): Promise<any> {
    return loadFn().catch(error => {
        if (maxRetries > 0) {
            console.warn(`Translation loading failed, retrying... (${maxRetries} attempts left)`, error);
            return new Promise(resolve => setTimeout(() => resolve(loadWithRetry(loadFn, maxRetries - 1)), 1000));
        }
        throw error;
    });
}

function appInitializerFactory(translate: TranslateService, injector: Injector) {
    return () =>
        new Promise<any>(async (resolve: any) => {
            try {
                const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
                await locationInitialized;

                const langToSet = 'en';
                const translationParts = GeneralHelper.getTranslationFiles();
                translate.setDefaultLang('en');

                await loadWithRetry(
                    () =>
                        new Promise<void>((resolve, reject) => {
                            translate.use(langToSet).subscribe({
                                next: () => resolve(),
                                error: reject,
                                complete: () => resolve(),
                            });
                        })
                );

                for (const part of translationParts) {
                    try {
                        await loadWithRetry(
                            () =>
                                new Promise<void>((resolve, reject) => {
                                    translate.getTranslation('en-' + part).subscribe({
                                        next: translations => {
                                            translate.setTranslation('en', translations, true);
                                            resolve();
                                        },
                                        error: reject,
                                    });
                                })
                        );
                    } catch (error) {
                        console.error(`Failed to load translation file 'en-${part}':`, error);
                    }
                }

                resolve(null);
            } catch (error) {
                console.error('Translation initialization failed:', error);
                resolve(null);
            }
        });
}

const authInterceptor = (req: HttpRequest<any>, handle: HttpHandlerFn) =>
    inject(AuthHttpInterceptor).intercept(req, { handle });
const auth0_audience = 'Tyx_AutomatedGenotyping_Api';
const auth0_cacheLocation = 'localstorage';

export const appConfig = {
    providers: [
        provideAnimations(),
        importProvidersFrom([
            TranslateModule.forRoot(provideTranslation()),
            AuthModule.forRoot({
                domain: environment.auth0.domain,
                clientId: environment.auth0.clientId,
                cacheLocation: auth0_cacheLocation,
                authorizationParams: {
                    redirect_uri: environment.auth0.redirectUri + '',
                    audience: auth0_audience,
                },
                httpInterceptor: {
                    allowedList: [
                        {
                            uri: `${environment.apiUrl}/order-results-shareable/*`,
                            allowAnonymous: true,
                        },
                        {
                            uri: `${environment.apiUrl}/*`,
                            tokenOptions: {
                                authorizationParams: {
                                    audience: auth0_audience,
                                },
                            },
                        },
                    ],
                },
            }),
        ]),

        {
            provide: APP_INITIALIZER,
            useFactory: appInitializerFactory,
            deps: [TranslateService, Injector],
            multi: true,
        },

        { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
        AuthHttpInterceptor,
        provideHttpClient(withInterceptors([authInterceptor, authenticationInterceptor])),

        provideRouter(routes, withInMemoryScrolling(scrollConfig), withPreloading(FlagBasedPreloadingStrategy)),

        MessageService,
    ],
};
